import { normalize, tokenize } from '@/lib/normalize'
import type { Book, Subject } from '@/lib/types'

/**
 * The client-side search index (SPECIFICATION.md §10).
 *
 * The whole catalogue is 371 records, so the index is a flat array of
 * pre-normalised field strings rather than an inverted index: building it costs
 * a few milliseconds and querying it stays well inside the 50 ms keystroke
 * budget (§15.5) without the memory cost of a posting list.
 */

/** §10.2 — field weights. */
const WEIGHTS = {
  title: 10,
  authors: 8,
  subjectNames: 3,
  keywords: 4,
  publisher: 2,
  description: 1,
} as const

type FieldName = keyof typeof WEIGHTS

export interface IndexedBook {
  book: Book
  fields: Record<FieldName, string>
  /** Tie-break input, refreshed from /stats without rebuilding the index. */
  downloadsTotal: number
}

export interface SearchIndex {
  entries: IndexedBook[]
  byId: Map<string, IndexedBook>
}

export interface SearchHit {
  book: Book
  score: number
  /** Tokens that matched the title or author line, for <mark> highlighting (§10.7). */
  matchedTokens: string[]
}

export function buildSearchIndex(books: Book[], subjects: Subject[]): SearchIndex {
  const subjectNameById = new Map<string, string>()
  for (const subject of subjects) {
    // Subject names are indexed in every language they exist in, so a Russian
    // query finds a discipline an admin has translated.
    const names = [subject.nameEn, subject.nameRu, subject.nameUz].filter(Boolean) as string[]
    subjectNameById.set(subject.id, names.join(' '))
  }

  const entries = books.map<IndexedBook>((book) => {
    const subjectNames = [...book.subjects.primary, ...book.subjects.related]
      .map((id) => subjectNameById.get(id) ?? '')
      .join(' ')

    return {
      book,
      downloadsTotal: 0,
      fields: {
        title: normalize(book.title),
        authors: normalize(book.authors.join(' ')),
        subjectNames: normalize(subjectNames),
        keywords: normalize(book.keywords.join(' ')),
        publisher: normalize(book.publisher ?? ''),
        description: '',
      },
    }
  })

  return { entries, byId: new Map(entries.map((entry) => [entry.book.id, entry])) }
}

/** Refreshes the download tie-break from the separately-cached /stats document. */
export function applyDownloadCounts(index: SearchIndex, counts: Map<string, number>): void {
  for (const entry of index.entries) {
    entry.downloadsTotal = counts.get(entry.book.id) ?? 0
  }
}

/**
 * Folds descriptions into the index once the detail document arrives.
 *
 * Descriptions are the lowest-weighted field (§10.2) and 35 KB gzipped — more
 * than half the entire first-paint budget in §15.5 — so the catalogue ships
 * without them and search upgrades itself a moment later rather than making
 * every reader wait. Until then a query still matches title, authors, keywords,
 * publisher and discipline names: five of the six indexed fields.
 */
export function applyDescriptions(index: SearchIndex, descriptions: Map<string, string>): void {
  for (const entry of index.entries) {
    entry.fields.description = normalize(descriptions.get(entry.book.id) ?? '')
  }
}

/**
 * Scores one entry against the query tokens.
 *
 * Every token must match somewhere (AND semantics, §10.3). The final token
 * matches by prefix so results update while the user is still typing.
 * Returns null when the entry does not match at all.
 */
function scoreEntry(
  entry: IndexedBook,
  tokens: string[],
  lastTokenIndex: number,
): { score: number; matched: string[] } | null {
  let score = 0
  const matched: string[] = []

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i]
    if (!token) continue
    const isLast = i === lastTokenIndex
    let tokenScore = 0
    let matchedInTitleOrAuthor = false

    for (const field of Object.keys(WEIGHTS) as FieldName[]) {
      const haystack = entry.fields[field]
      if (haystack.length === 0) continue

      const exact = hasWholeWord(haystack, token)
      const partial = exact || (isLast && haystack.includes(token))
      if (!partial) continue

      // §10.4 — an exact whole-word hit is worth double a prefix hit.
      tokenScore = Math.max(tokenScore, WEIGHTS[field] * (exact ? 2 : 1))
      if (field === 'title' || field === 'authors') matchedInTitleOrAuthor = true
    }

    // AND semantics: one unmatched token disqualifies the record.
    if (tokenScore === 0) return null
    score += tokenScore
    if (matchedInTitleOrAuthor) matched.push(token)
  }

  // §10.4 — phrase bonus when the tokens appear in order inside one field.
  const phrase = tokens.join(' ')
  if (tokens.length > 1) {
    if (entry.fields.title.includes(phrase)) score += WEIGHTS.title
    else if (entry.fields.authors.includes(phrase)) score += WEIGHTS.authors
  }

  return { score, matched }
}

function hasWholeWord(haystack: string, token: string): boolean {
  let from = 0
  for (;;) {
    const at = haystack.indexOf(token, from)
    if (at === -1) return false
    const before = at === 0 ? ' ' : haystack[at - 1]
    const afterIndex = at + token.length
    const after = afterIndex >= haystack.length ? ' ' : haystack[afterIndex]
    if ((before === ' ' || before === '-') && (after === ' ' || after === '-')) return true
    from = at + 1
  }
}

/**
 * Runs a query against the index. An empty query returns every book with a
 * zero score, so callers can apply filters and sorting uniformly.
 */
export function search(index: SearchIndex, query: string): SearchHit[] {
  const tokens = tokenize(query)
  if (tokens.length === 0) {
    return index.entries.map((entry) => ({ book: entry.book, score: 0, matchedTokens: [] }))
  }

  const lastTokenIndex = tokens.length - 1
  const hits: SearchHit[] = []

  for (const entry of index.entries) {
    const result = scoreEntry(entry, tokens, lastTokenIndex)
    if (!result) continue
    hits.push({
      book: entry.book,
      // §10.4 — downloads break ties, scaled far below any weight difference.
      score: result.score + Math.min(entry.downloadsTotal, 10_000) / 1_000_000,
      matchedTokens: result.matched,
    })
  }

  return hits
}

/**
 * Splits `text` into the runs that matched and the runs that did not, so the
 * results list can wrap matches in <mark> without using dangerouslySetInnerHTML
 * (§10.7).
 */
export function highlightSegments(
  text: string,
  tokens: string[],
): Array<{ text: string; match: boolean }> {
  if (tokens.length === 0) return [{ text, match: false }]

  const normalizedText = normalize(text)
  // normalize() can change length (diacritics decompose), so ranges are only
  // trustworthy when the transform was length-preserving. Fall back to no
  // highlighting rather than mis-slicing a title.
  if (normalizedText.length !== text.length) return [{ text, match: false }]

  const ranges: Array<[number, number]> = []
  for (const token of tokens) {
    let from = 0
    for (;;) {
      const at = normalizedText.indexOf(token, from)
      if (at === -1) break
      ranges.push([at, at + token.length])
      from = at + token.length
    }
  }
  if (ranges.length === 0) return [{ text, match: false }]

  ranges.sort((a, b) => a[0] - b[0])
  const merged: Array<[number, number]> = []
  for (const range of ranges) {
    const last = merged[merged.length - 1]
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1])
    else merged.push([range[0], range[1]])
  }

  const segments: Array<{ text: string; match: boolean }> = []
  let cursor = 0
  for (const [start, end] of merged) {
    if (start > cursor) segments.push({ text: text.slice(cursor, start), match: false })
    segments.push({ text: text.slice(start, end), match: true })
    cursor = end
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), match: false })
  return segments
}
